import { useState, useRef } from "react";
import { Upload, FileText, X, Download, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACCEPT = "*/*";   // all file types allowed
const MAX_MB = 10;

/**
 * Uploader for the customer RFQ mail (mandatory, like the drawing). Mirrors
 * DrawingUpload but for a generic file (no thumbnail). Hits /npd/{id}/rfq-mail.
 */
export default function RfqMailUpload({ npdId, rfq, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File too large (max ${MAX_MB} MB)`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/npd/${npdId}/rfq-mail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("RFQ mail uploaded");
      onChanged && onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onUpload = (ev) => uploadFile(ev.target.files?.[0]);
  const onDrop = (ev) => {
    ev.preventDefault(); setDrag(false);
    if (!canEdit || busy) return;
    uploadFile(ev.dataTransfer?.files?.[0]);
  };
  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); if (!drag) setDrag(true); },
    onDragLeave: (e) => { e.preventDefault(); setDrag(false); },
    onDrop,
  };

  const onDelete = async () => {
    if (!window.confirm("Remove RFQ mail?")) return;
    try {
      await api.delete(`/npd/${npdId}/rfq-mail`);
      toast.success("RFQ mail removed");
      onChanged && onChanged(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const onDownload = async () => {
    try {
      const r = await api.get(`/npd/${npdId}/rfq-mail`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rfq?.rfq_mail_original_filename || "rfq-mail";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const has = !!rfq?.rfq_mail_path;

  return (
    <div className="col-span-2" data-testid="rfq-mail-upload">
      {has ? (
        <div className="p-4 border border-slate-200 rounded-md bg-slate-50">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 bg-white border border-slate-200 rounded-md flex-shrink-0 flex items-center justify-center">
              <Mail size={24} className="text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText size={14} className="text-slate-500 shrink-0" />
                <span className="text-sm font-medium text-slate-900 truncate">{rfq.rfq_mail_original_filename}</span>
              </div>
              <div className="text-xs text-slate-500">
                {rfq.rfq_mail_size_bytes ? `${(rfq.rfq_mail_size_bytes / 1024).toFixed(1)} KB` : "—"}
                {rfq.rfq_mail_uploaded_at && (<> · uploaded {new Date(rfq.rfq_mail_uploaded_at).toLocaleDateString("en-IN")}</>)}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-2 border-t border-slate-200 flex flex-wrap items-center gap-3">
            <button type="button" onClick={onDownload} className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">
              <Download size={12} /> Download
            </button>
            {canEdit && (
              <button type="button" onClick={onDelete} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={12} /> Remove
              </button>
            )}
            {canEdit && (
              <label className="text-xs text-slate-600 hover:text-slate-900 cursor-pointer flex items-center gap-1">
                <Upload size={12} /> Replace
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
              </label>
            )}
          </div>
        </div>
      ) : canEdit ? (
        <label {...dragProps} className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${drag ? "border-orange-500 bg-orange-50" : "border-slate-300 hover:border-orange-400 bg-slate-50 hover:bg-orange-50/30"}`}>
          {busy ? (
            <><Loader2 size={28} className="text-orange-500 animate-spin mb-2" /><span className="text-sm text-slate-600">Uploading…</span></>
          ) : (
            <>
              <Upload size={28} className="text-slate-400 mb-2" />
              <span className="text-sm font-medium text-slate-700">{drag ? "Drop file to upload" : "Click or drag & drop RFQ mail"}</span>
              <span className="text-xs text-slate-500 mt-1">Any file type · max {MAX_MB} MB</span>
            </>
          )}
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onUpload} disabled={busy} />
        </label>
      ) : (
        <div className="border border-slate-200 rounded-md p-6 text-center text-sm text-slate-400 bg-slate-50">No RFQ mail uploaded</div>
      )}
    </div>
  );
}
